import React, { useEffect, useState } from "react";
import { Upload, Shield, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";

async function toBase64(file) {
  if (file.size > 3 * 1024 * 1024) throw new Error(`${file.name} > 3MB`);
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function OwnerVerify() {
  const { user, refresh } = useAuth();
  const [aadhaar, setAadhaar] = useState("");
  const [pan, setPan] = useState("");
  const [businessProof, setBusinessProof] = useState("");
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    api.get("/verification/me").then(({ data }) => {
      if (data?.status && data.status !== "none") setExisting(data);
    }).catch(() => {});
  }, []);

  const upload = async (e, setter) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setter(await toBase64(f)); }
    catch (err) { toast.error(err.message); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!aadhaar || !pan) return toast.error("Aadhaar and PAN photos are required");
    setLoading(true);
    try {
      await api.post("/verification/submit", {
        aadhaar_photo: aadhaar, pan_photo: pan,
        business_proof: businessProof || null,
        business_name: businessName || null,
        notes: notes || null,
      });
      toast.success("Verification submitted. Admin will review shortly.");
      await refresh();
      const { data } = await api.get("/verification/me");
      setExisting(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const StatusIcon = existing?.status === "approved" ? CheckCircle2 : existing?.status === "rejected" ? XCircle : Clock;
  const statusColor = existing?.status === "approved" ? "text-[#2B3A67]" : existing?.status === "rejected" ? "text-red-600" : "text-[#C85A32]";

  return (
    <div className="max-w-2xl mx-auto px-5 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Owner verification</div>
      <h1 className="font-display text-4xl mt-2">Get the Verified badge</h1>
      <p className="text-[#5C6B7A] mt-2">
        Aadhaar + PAN are required for all owners. To list <b>commercial</b> categories (Shop, Factory, Cafe/Restaurant, Warehouse) you also need a GST/Shop-Act business proof.
      </p>

      {existing && (
        <div className={`mt-6 rounded-lg border p-4 flex items-start gap-3 ${existing.status === "approved" ? "border-[#2B3A67]/30 bg-[#D4DEC6]/30" : existing.status === "rejected" ? "border-red-300 bg-red-50" : "border-[#C85A32]/30 bg-[#F0EBE1]"}`} data-testid="verify-status-banner">
          <StatusIcon className={statusColor} />
          <div className="flex-1">
            <div className={`font-semibold ${statusColor}`}>
              Current status: {existing.status?.toUpperCase()}
            </div>
            <div className="text-sm text-[#5C6B7A] mt-1">
              Submitted on {new Date(existing.submitted_at).toLocaleDateString()}
              {existing.reviewed_at && ` · Reviewed on ${new Date(existing.reviewed_at).toLocaleDateString()}`}
            </div>
            {existing.review_notes && <div className="text-sm mt-2"><b>Admin note:</b> {existing.review_notes}</div>}
            {existing.status === "rejected" && <div className="text-sm mt-2 text-red-600">You can resubmit below.</div>}
            {existing.status === "approved" && <div className="text-sm mt-2 text-[#2B3A67]">Verified badge is now active on all your listings. 🎉</div>}
          </div>
        </div>
      )}

      {(!existing || existing.status !== "approved") && (
        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="verify-form">
          <UploadBlock label="Aadhaar photo *" testid="upload-aadhaar" value={aadhaar} onFile={(e) => upload(e, setAadhaar)} onClear={() => setAadhaar("")} />
          <UploadBlock label="PAN photo *" testid="upload-pan" value={pan} onFile={(e) => upload(e, setPan)} onClear={() => setPan("")} />
          <div>
            <Label>Business name (required for commercial listings)</Label>
            <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Sharma Realty Ventures" data-testid="verify-business-name" />
          </div>
          <UploadBlock label="Business proof — GST / Shop-Act license (optional; earns the Business-verified badge for Shop, Factory, Cafe, Warehouse)" testid="upload-biz" value={businessProof} onFile={(e) => upload(e, setBusinessProof)} onClear={() => setBusinessProof("")} />
          <div>
            <Label>Notes for admin (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="verify-notes" />
          </div>
          <div className="flex items-center gap-2 text-xs text-[#5C6B7A]">
            <Shield size={14} /> Your documents are only viewable by LocaNest admins.
          </div>
          <Button type="submit" disabled={loading} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11 px-8" data-testid="verify-submit">
            {loading ? "Submitting…" : (existing?.status === "rejected" ? "Resubmit" : "Submit for review")}
          </Button>
        </form>
      )}
    </div>
  );
}

function UploadBlock({ label, testid, value, onFile, onClear }) {
  return (
    <div>
      <Label>{label}</Label>
      {value ? (
        <div className="mt-2 relative w-40 h-40 rounded-md overflow-hidden border border-[#1A1F2C]/10">
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={onClear} className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full text-xs" data-testid={`${testid}-clear`}>×</button>
        </div>
      ) : (
        <label className="mt-2 w-40 h-40 rounded-md border-2 border-dashed border-[#1A1F2C]/20 flex flex-col items-center justify-center text-xs text-[#5C6B7A] cursor-pointer hover:border-[#C85A32] hover:text-[#C85A32]" data-testid={testid}>
          <Upload size={20} className="mb-1" /> Upload photo
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
      )}
    </div>
  );
}
